import { useEffect, useState, useMemo, useCallback } from "react";
import { AlertCircle, ChevronDown, ChevronRight, Download } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import {
  fetchOccupancyAudit,
  type MonthlyOccupancyAudit,
} from "../../api/client";
import { fmtPercent, fmtSF, fmtNumber, fmtDateShort } from "../../utils/format";
import { downloadCSV } from "../../utils/csv";
import LoadingSpinner from "../LoadingSpinner";

// ---------- types ----------

interface OccupancyTabProps {
  dealId: string;
  scenarioId: string;
}

// ---------- helpers ----------

function monthLabel(rec: MonthlyOccupancyAudit): string {
  return fmtDateShort(`${rec.calendar_year}-${String(rec.month).padStart(2, "0")}-01`);
}

interface ChartRow {
  label: string;
  occupied: number;
  vacant: number;
  total: number;
}

// ---------- Expandable table row ----------

function OccupancyRow({ rec }: { rec: MonthlyOccupancyAudit }) {
  const [expanded, setExpanded] = useState(false);
  const Chevron = expanded ? ChevronDown : ChevronRight;
  const vacant = rec.total_leasable_sf - rec.occupied_sf;

  return (
    <>
      <tr
        className="border-b border-grove-200 last:border-0 hover:bg-grove-50 transition-colors cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-4 py-2.5 text-xs text-bark-700 whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            <Chevron className="w-3.5 h-3.5 text-bark-400" />
            {monthLabel(rec)}
          </div>
        </td>
        <td className="px-4 py-2.5 text-xs text-bark-700 tabular-nums text-right whitespace-nowrap">
          {fmtPercent(rec.occupancy_rate, 1)}
        </td>
        <td className="px-4 py-2.5 text-xs text-bark-600 tabular-nums text-right whitespace-nowrap">
          {fmtNumber(rec.occupied_sf)}
        </td>
        <td className="px-4 py-2.5 text-xs text-bark-600 tabular-nums text-right whitespace-nowrap">
          {fmtNumber(vacant)}
        </td>
        <td className="px-4 py-2.5 text-xs text-bark-600 tabular-nums text-right whitespace-nowrap">
          {rec.tenants_occupied.length > 0
            ? `+${rec.tenants_occupied.length}`
            : "\u2014"}
        </td>
        <td className="px-4 py-2.5 text-xs text-bark-600 tabular-nums text-right whitespace-nowrap">
          {rec.tenants_vacant.length > 0
            ? rec.tenants_vacant.length
            : "\u2014"}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-grove-50/50">
          <td colSpan={6} className="px-4 py-3">
            <div className="ml-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              {rec.tenants_occupied.length > 0 && (
                <div>
                  <h6 className="text-[11px] font-semibold text-forest-700 uppercase tracking-wider mb-1">
                    Occupied
                  </h6>
                  <ul className="space-y-0.5 text-bark-600">
                    {rec.tenants_occupied.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}
              {rec.tenants_vacant.length > 0 && (
                <div>
                  <h6 className="text-[11px] font-semibold text-red-600 uppercase tracking-wider mb-1">
                    Vacant
                  </h6>
                  <ul className="space-y-0.5 text-bark-600">
                    {rec.tenants_vacant.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------- Chart ----------

function OccupancyChart({ chartData, avgTotal }: { chartData: ChartRow[]; avgTotal: number }) {
  return (
    <div className="bg-white border border-grove-300 rounded-xl p-5 shadow-sm">
      <h4 className="text-sm font-semibold text-bark-700 mb-4">
        Occupancy Over Time
      </h4>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10, fill: "#6B6B60" }}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 10, fill: "#6B6B60" }}
            tickFormatter={(v: number) => fmtNumber(v)}
          />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={(value: any, name: any) => [
              fmtSF(Number(value) || 0),
              name === "occupied" ? "Occupied" : "Vacant",
            ]}
            labelStyle={{ fontSize: 11, fontWeight: 600 }}
            contentStyle={{ fontSize: 11 }}
          />
          <Legend
            wrapperStyle={{ fontSize: 11 }}
            formatter={(value: string) =>
              value === "occupied" ? "Occupied SF" : "Vacant SF"
            }
          />
          <ReferenceLine y={avgTotal} stroke="#D4D0C8" strokeDasharray="3 3" />
          <Bar dataKey="occupied" stackId="a" fill="#2D5016" radius={[0, 0, 0, 0]} />
          <Bar dataKey="vacant" stackId="a" fill="#DC2626" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------- Main component ----------

export default function OccupancyTab({ dealId, scenarioId }: OccupancyTabProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<MonthlyOccupancyAudit[]>([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchOccupancyAudit(dealId, scenarioId);
      const sorted = result.sort((a, b) => {
        if (a.calendar_year !== b.calendar_year) return a.calendar_year - b.calendar_year;
        return a.month - b.month;
      });
      setData(sorted);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Failed to load occupancy audit");
    } finally {
      setLoading(false);
    }
  }, [dealId, scenarioId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const chartData = useMemo<ChartRow[]>(
    () =>
      data.map((r) => ({
        label: monthLabel(r),
        occupied: r.occupied_sf,
        vacant: r.total_leasable_sf - r.occupied_sf,
        total: r.total_leasable_sf,
      })),
    [data],
  );

  const avgTotal = useMemo(() => {
    if (data.length === 0) return 0;
    return data.reduce((s, r) => s + r.total_leasable_sf, 0) / data.length;
  }, [data]);

  const avgOccupancy = useMemo(() => {
    if (data.length === 0) return 0;
    return data.reduce((s, r) => s + r.occupancy_rate, 0) / data.length;
  }, [data]);

  if (loading) return <LoadingSpinner />;

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-700 text-sm">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        {error}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white border border-grove-300 rounded-xl p-8 text-center">
        <p className="text-sm text-bark-400">No occupancy audit data available.</p>
      </div>
    );
  }

  function exportOccupancyCSV() {
    const headers: (string | number)[] = ["Month", "Occupancy %", "Occupied SF", "Vacant SF", "Tenants Occupied", "Tenants Vacant"];
    const rows = data.map((rec) => [
      monthLabel(rec),
      fmtPercent(rec.occupancy_rate, 1),
      rec.occupied_sf,
      rec.total_leasable_sf - rec.occupied_sf,
      rec.tenants_occupied.join("; "),
      rec.tenants_vacant.join("; "),
    ]);
    downloadCSV([headers, ...rows], "audit_occupancy.csv");
  }

  const thCls = "text-[11px] font-semibold text-bark-400 uppercase tracking-wider";

  return (
    <div className="space-y-6">
      {/* Download button */}
      <div className="flex justify-end print:hidden">
        <button
          onClick={exportOccupancyCSV}
          className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-bark-500 bg-white border border-grove-300 rounded-lg hover:bg-grove-50 hover:shadow-sm transition-all"
        >
          <Download className="w-4 h-4" />
          CSV
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-grove-300 rounded-xl p-4">
          <p className="text-xs font-medium text-bark-400 uppercase tracking-wider">
            Avg Occupancy
          </p>
          <p className="text-2xl font-semibold text-bark-800 tabular-nums mt-1">
            {fmtPercent(avgOccupancy, 1)}
          </p>
        </div>
        <div className="bg-white border border-grove-300 rounded-xl p-4">
          <p className="text-xs font-medium text-bark-400 uppercase tracking-wider">
            Total Leasable
          </p>
          <p className="text-2xl font-semibold text-bark-800 tabular-nums mt-1">
            {fmtSF(data[0]?.total_leasable_sf)}
          </p>
        </div>
        <div className="bg-white border border-grove-300 rounded-xl p-4">
          <p className="text-xs font-medium text-bark-400 uppercase tracking-wider">
            Months Tracked
          </p>
          <p className="text-2xl font-semibold text-bark-800 tabular-nums mt-1">
            {data.length}
          </p>
        </div>
      </div>

      {/* Stacked bar chart */}
      <OccupancyChart chartData={chartData} avgTotal={avgTotal} />

      {/* Detail table */}
      <div className="bg-white border border-grove-300 rounded-xl overflow-hidden shadow-sm">
        <div className="px-4 py-3 bg-grove-50 border-b border-grove-200">
          <h4 className="text-sm font-semibold text-bark-700">Monthly Detail</h4>
          <p className="text-xs text-bark-400 mt-0.5">
            Click a row to see tenant names
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-grove-200 bg-grove-50/50">
                <th className={`text-left px-4 py-2.5 ${thCls}`}>Month</th>
                <th className={`text-right px-4 py-2.5 ${thCls}`}>Occupancy %</th>
                <th className={`text-right px-4 py-2.5 ${thCls}`}>Occupied SF</th>
                <th className={`text-right px-4 py-2.5 ${thCls}`}>Vacant SF</th>
                <th className={`text-right px-4 py-2.5 ${thCls}`}>Tenants In</th>
                <th className={`text-right px-4 py-2.5 ${thCls}`}>Tenants Out</th>
              </tr>
            </thead>
            <tbody>
              {data.map((rec) => (
                <OccupancyRow
                  key={`${rec.calendar_year}-${rec.month}`}
                  rec={rec}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
